/**
 * Per-provider channel config form — driven by PROVIDER_META registry (DRY).
 * Secret fields show a reveal toggle; on edit, omit secret keys from payload
 * unless the user re-enters them (BE keeps the encrypted value).
 *
 * C1 safety: secret fields MUST be seeded to '' (empty) in edit mode, never
 * from the masked value BE returns (e.g. "5824****4bs"). buildConfigPayload
 * also defensively skips any value containing "****" to prevent accidentally
 * re-sending a masked echo.
 */
import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Input } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import type { ChannelType } from '@/api/healthcheck';
import { PROVIDER_META, type ProviderField } from './channel-provider-meta';

export type ProviderConfigState = Record<string, string | number | undefined>;

interface Props {
  type: ChannelType;
  config: ProviderConfigState;
  onChange: (patch: ProviderConfigState) => void;
  showErrors?: boolean;
  /** When true, secret fields show "unchanged" placeholder instead of requiring input. */
  isEditing?: boolean;
}

/** Validate required fields for a given provider type. */
export function isProviderConfigValid(type: ChannelType, config: ProviderConfigState): boolean {
  const meta = PROVIDER_META[type];
  return meta.fields
    .filter((f) => f.required)
    .every((f) => {
      const v = config[f.name];
      // In edit mode a required secret field may be left empty (unchanged) — skip validation.
      if (meta.secretFields.includes(f.name)) return true;
      return v !== undefined && v !== '' && v !== null;
    });
}

/** Returns true if value looks like a BE mask sentinel (never send these back). */
function isMasked(v: string | number | undefined): boolean {
  return typeof v === 'string' && v.includes('****');
}

/**
 * Build the config payload to send to BE.
 *
 * In edit mode:
 * - Omit secret fields when empty (user didn't re-enter → BE keeps encrypted value).
 * - Also omit any field whose value contains "****" (masked echo from GET — defensive guard).
 *
 * M1 fix: number coercion uses Number.isFinite to avoid dropping valid 0.
 */
export function buildConfigPayload(
  type: ChannelType,
  config: ProviderConfigState,
  isEditing = false,
): Record<string, unknown> {
  const meta = PROVIDER_META[type];
  const out: Record<string, unknown> = {};
  for (const field of meta.fields) {
    const v = config[field.name];
    const isSecret = meta.secretFields.includes(field.name);

    // Omit empty secret fields in edit mode — BE keeps the encrypted value.
    if (isEditing && isSecret && (v === undefined || v === '')) continue;

    // Defensive: never send a masked value back (C1 guard).
    if (isMasked(v)) continue;

    if (v !== undefined && v !== '') {
      if (field.type === 'number') {
        const n = Number(v);
        // M1 fix: use isFinite to avoid dropping 0 (Number(0) || undefined === undefined).
        out[field.name] = Number.isFinite(n) ? n : undefined;
      } else {
        out[field.name] = v;
      }
    }
  }
  return out;
}

/**
 * Seed form state for EDIT mode from a masked BE config response.
 * Secret fields → '' (empty). Non-secret fields keep their value UNLESS
 * masked (corrupted partial display like "-425****024" → also cleared).
 */
export function seedEditConfig(
  type: ChannelType,
  rawConfig: Record<string, unknown>,
): ProviderConfigState {
  const meta = PROVIDER_META[type];
  return Object.fromEntries(
    meta.fields.map((f) => {
      const v = rawConfig[f.name];
      const isSecret = meta.secretFields.includes(f.name);
      // Always blank secrets so masked string is never in the form state.
      if (isSecret) return [f.name, ''];
      // Non-secrets: blank if masked (prevents sending corrupted partial values).
      if (isMasked(v as string | number | undefined)) return [f.name, ''];
      return [f.name, v as string | undefined];
    }),
  );
}

/** Single field row with optional secret reveal toggle. */
function ProviderFieldInput({
  field,
  value,
  onChange,
  showErrors,
  isEditing,
}: {
  field: ProviderField;
  value: string;
  onChange: (v: string) => void;
  showErrors?: boolean;
  isEditing?: boolean;
}) {
  const [revealed, setRevealed] = useState(false);
  const isSecret = field.type === 'password';
  const isEmpty = !value;
  // In edit mode, required secrets are optional (user leaves blank = keep existing).
  const hasError = showErrors && field.required && isEmpty && !(isEditing && isSecret);

  const placeholder = isEditing && isSecret ? '••• (unchanged)' : (field.placeholder ?? '');
  const inputType = isSecret && !revealed ? 'password' : 'text';

  return (
    <Field
      label={field.label}
      required={field.required && !(isEditing && isSecret)}
      description={field.helper}
      error={hasError ? `${field.label} is required.` : undefined}
    >
      {(p) => (
        <Input
          {...p}
          type={inputType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          invalid={hasError}
          style={{ maxWidth: 420 }}
          endSlot={
            isSecret ? (
              <button
                type="button"
                onClick={() => setRevealed((r) => !r)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  color: 'var(--vn-fg-muted)',
                  display: 'flex',
                  alignItems: 'center',
                }}
                aria-label={revealed ? 'Hide' : 'Reveal'}
              >
                {revealed ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            ) : undefined
          }
        />
      )}
    </Field>
  );
}

/** Renders all config fields for a given provider type from the meta registry. */
export function ChannelProviderConfigForm({ type, config, onChange, showErrors, isEditing }: Props) {
  const meta = PROVIDER_META[type];
  const str = (k: string) => (config[k] === undefined || config[k] === null ? '' : String(config[k]));

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <ProviderSetupGuide type={type} />
      {meta.fields.map((field) => (
        <ProviderFieldInput
          key={field.name}
          field={field}
          value={str(field.name)}
          onChange={(v) => onChange({ [field.name]: v })}
          showErrors={showErrors}
          isEditing={isEditing}
        />
      ))}
    </div>
  );
}

/** Info panel showing setup steps and optional docs link for a provider. */
function ProviderSetupGuide({ type }: { type: ChannelType }) {
  const meta = PROVIDER_META[type];

  return (
    <Alert tone="info" title={`How to set up ${meta.label}`}>
      <ol style={{ margin: '4px 0 0', paddingLeft: 'var(--vn-space-4)', display: 'grid', gap: 2 }}>
        {meta.setupSteps.map((step, i) => (
          <li key={i} style={{ fontSize: 'var(--vn-font-size-sm)' }}>{step}</li>
        ))}
      </ol>
      {meta.docsUrl && (
        <a
          href={meta.docsUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'inline-block', marginTop: 'var(--vn-space-2)', fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-info-text)' }}
        >
          Learn more ↗
        </a>
      )}
    </Alert>
  );
}
